import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Search, FileSearch, ScanLine,
  CheckCircle2, Clock, AlertCircle, Image as ImageIcon, X, Download,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STATUS_FILTERS = [
  { value: 'all', label: 'All entries' },
  { value: 'pending_section', label: 'Pending in section' },
  { value: 'pending_accounts', label: 'Pending in accounts' },
  { value: 'completed', label: 'Completed' },
];

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Parcel-type display labels. Types outside the accounts scan workload
// (document / internal transfer / sample / job-worker return) show a muted
// badge and their "Confirm in Accounts" box is disabled.
const PARCEL_TYPE_LABELS = {
  goods: 'Goods',
  document: 'Document',
  trims: 'Trims',
  warehouse_transfer: 'Internal Transfer',
  sample: 'Sample',
  job_worker_return: 'Job Worker Return',
};
const parcelLabel = (pt) =>
  PARCEL_TYPE_LABELS[(pt || '').toLowerCase()] || (pt ? pt.replace(/_/g, ' ') : '—');

// Build the default date range = current month (first of month → today,
// inclusive). Stored as `YYYY-MM-DD` strings to match <input type="date">.
const defaultFromDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};
const defaultToDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const statusBadge = (row) => {
  if (row.is_completed) {
    return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Completed</Badge>;
  }
  if (row.is_pending_accounts) {
    return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Awaiting Accounts</Badge>;
  }
  if (row.is_pending_section) {
    return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Pending in Section</Badge>;
  }
  return <Badge variant="outline">Open</Badge>;
};

const GateEntryDetails = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending_section: 0, pending_accounts: 0, completed: 0 });
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [fromDate, setFromDate] = useState(defaultFromDate());
  const [toDate, setToDate] = useState(defaultToDate());

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Per-row scan state
  const [scanInputs, setScanInputs] = useState({}); // entry_id -> scanned value
  const [submittingId, setSubmittingId] = useState(null);
  const rowRefs = useRef({});

  // Image preview modal
  const [imgEntry, setImgEntry] = useState(null);
  const [imgUrls, setImgUrls] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);

  // CSV export state
  const [exporting, setExporting] = useState(false);

  // Distinct section list (used in the section filter).
  const sections = Array.from(new Set(rows.map((r) => r.assigned_section).filter(Boolean)));

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, page, page_size: pageSize };
      if (sectionFilter && sectionFilter !== 'all') params.section = sectionFilter;
      if (searchDebounced) params.search = searchDebounced;
      // Date range is inclusive — translate to ISO timestamps so the
      // backend `$gte` / `$lte` filter on `inward_entries.timestamp`
      // catches every entry on `toDate` day too.
      if (fromDate) params.from_date = `${fromDate}T00:00:00`;
      if (toDate) params.to_date = `${toDate}T23:59:59`;
      const r = await apiClient.get('/accounts/gate-entry-details', { params });
      setRows(r.data.rows || []);
      setCounts(r.data.counts || {});
      setTotalCount(r.data.total_count || 0);
      setTotalPages(r.data.total_pages || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load gate entries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sectionFilter, searchDebounced, fromDate, toDate, page, pageSize]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Reset to page 1 whenever filters change.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sectionFilter, searchDebounced, fromDate, toDate, pageSize]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: '50000' });
      if (sectionFilter && sectionFilter !== 'all') params.append('section', sectionFilter);
      if (searchDebounced) params.append('search', searchDebounced);
      if (fromDate) params.append('from_date', `${fromDate}T00:00:00`);
      if (toDate) params.append('to_date', `${toDate}T23:59:59`);
      // apiClient already injects the auth + site headers and uses the
      // REACT_APP_BACKEND_URL base. We need a *blob* response for the
      // CSV download though, so call with responseType: 'blob'.
      const r = await apiClient.get(
        `/accounts/gate-entry-details/export.csv?${params.toString()}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      a.download = `gate-entry-details_${statusFilter}_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const openImageDialog = async (entryId) => {
    setImgEntry(entryId);
    setImgUrls([]);
    setImgLoading(true);
    try {
      const r = await apiClient.get(`/accounts/gate-entry-details/${entryId}/signed-images`);
      setImgUrls(r.data.signed_urls || []);
    } catch (e) {
      toast.error('Failed to load images');
    } finally {
      setImgLoading(false);
    }
  };

  const updateScan = (entryId, value) => {
    setScanInputs((prev) => ({ ...prev, [entryId]: value }));
  };

  const confirmReceipt = async (row, e) => {
    e?.preventDefault?.();
    const scanned = (scanInputs[row.entry_id] || '').trim();
    if (!scanned) {
      toast.error('Scan or type the document number first');
      return;
    }
    // Strict match per Q1=b — if the scanned value belongs to a different
    // visible row, jump to that row instead of accepting the mismatch.
    if (scanned !== row.document_barcode) {
      const target = rows.find((r) => r.document_barcode === scanned);
      if (target && target.entry_id !== row.entry_id) {
        toast.warning(
          `Scanned ${scanned} doesn't match this row — jumping to its matching row.`,
        );
        setScanInputs((prev) => ({ ...prev, [target.entry_id]: scanned, [row.entry_id]: '' }));
        const el = rowRefs.current[target.entry_id];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-amber-400');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 1800);
          // Auto-focus the matching row's scan input.
          const input = el.querySelector('input[data-testid^="scan-input-"]');
          input?.focus();
        }
        return;
      }
      toast.error(`Scanned barcode does not match. Expected ${row.document_barcode}.`);
      return;
    }

    setSubmittingId(row.entry_id);
    try {
      const res = await apiClient.post(
        `/accounts/gate-entry-details/${row.entry_id}/confirm-receipt`,
        { scanned_document: scanned },
      );
      toast.success(res.data.message || 'Receipt confirmed');
      setScanInputs((prev) => ({ ...prev, [row.entry_id]: '' }));
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm receipt');
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/accounts')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-to-accounts">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold">Gate Entry Details</h1>
              <p className="text-xs text-stone-300">Accounts · Inward invoice tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={exportCsv}
              disabled={exporting || loading}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="export-csv-btn"
            >
              <Download className={`w-4 h-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button variant="ghost" onClick={fetchRows} disabled={loading} className="text-[#F9FAFB] hover:bg-white/10" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Status counter chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="status-counters">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('all')}>
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wide text-stone-500">All entries</p>
              <p className="text-2xl font-semibold text-[#1A1F2E]">{counts.all || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md border-rose-200" onClick={() => setStatusFilter('pending_section')}>
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wide text-rose-600">Pending in section</p>
              <p className="text-2xl font-semibold text-rose-700">{counts.pending_section || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md border-amber-200" onClick={() => setStatusFilter('pending_accounts')}>
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wide text-amber-700">Pending in accounts</p>
              <p className="text-2xl font-semibold text-amber-800">{counts.pending_accounts || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md border-emerald-200" onClick={() => setStatusFilter('completed')}>
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wide text-emerald-600">Completed</p>
              <p className="text-2xl font-semibold text-emerald-700">{counts.completed || 0}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-[#2D5F4F]" /> Inward Gate Entries
            </CardTitle>
            <CardDescription>
              Each row merges gate inward + inward documentation + section bundle-receipt status. Scan the printed document barcode on a row to confirm the invoice has reached Accounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-4">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_FILTERS.map((s) => (
                      <SelectItem key={s.value} value={s.value} data-testid={`filter-status-${s.value}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Section</Label>
                <Select value={sectionFilter} onValueChange={setSectionFilter}>
                  <SelectTrigger data-testid="filter-section"><SelectValue placeholder="All sections" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sections</SelectItem>
                    {sections.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> From
                </Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-9"
                  data-testid="filter-from-date"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> To
                </Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="h-9"
                  data-testid="filter-to-date"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Search (document, vendor, vehicle)</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="DOC000123/26-27 or vendor name…"
                    className="pl-8"
                    data-testid="search-input"
                  />
                </div>
                {searchDebounced && (
                  <p className="mt-1 text-[11px] text-amber-700" data-testid="search-all-dates-hint">
                    Searching all dates — the date range is ignored while a search is active.
                  </p>
                )}
              </div>
            </div>

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[150px]">Document</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Section</TableHead>
                    <TableHead>Bundles / Qty</TableHead>
                    <TableHead>Gate</TableHead>
                    <TableHead>Doc Confirmed</TableHead>
                    <TableHead>Section Received</TableHead>
                    <TableHead>Accounts</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="min-w-[270px]">Confirm in Accounts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-12 text-stone-500">
                      <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                    </TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-12 text-stone-400">
                      No entries match the current filters.
                    </TableCell></TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow
                        key={r.entry_id}
                        ref={(el) => { rowRefs.current[r.entry_id] = el; }}
                        className={r.is_completed ? 'bg-emerald-50/40' : ''}
                        data-testid={`gate-entry-row-${r.entry_id}`}
                      >
                        <TableCell>
                          <div className="font-mono text-sm">{r.document_barcode || '—'}</div>
                          <div className="text-[10px] text-stone-400">{r.entry_id}</div>
                          {r.parcel_type && (
                            <span
                              className={`mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded ${r.accounts_confirmable ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}
                              data-testid={`parcel-type-${r.entry_id}`}
                            >
                              {parcelLabel(r.parcel_type)}
                            </span>
                          )}
                          {r.image_count > 0 && (
                            <button
                              onClick={() => openImageDialog(r.entry_id)}
                              className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                              data-testid={`view-images-${r.entry_id}`}
                            >
                              <ImageIcon className="w-3 h-3" /> {r.image_count} image{r.image_count > 1 ? 's' : ''}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {r.vendor_name || '—'}
                          {r.vehicle_no && <div className="text-[10px] text-stone-400">Vehicle: {r.vehicle_no}</div>}
                        </TableCell>
                        <TableCell>
                          {r.assigned_section
                            ? <Badge variant="outline" className="text-xs">{r.assigned_section}</Badge>
                            : <span className="text-xs text-stone-400">unassigned</span>}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{r.bundle_count || 0} bundles</div>
                          {(r.invoice_qty || r.invoice_value) && (
                            <div className="text-[10px] text-stone-400">
                              {r.invoice_qty != null && <>Qty {r.invoice_qty} · </>}
                              {r.invoice_value != null && <>₹{r.invoice_value}</>}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{fmt(r.gate_at)}</TableCell>
                        <TableCell className="text-xs">{fmt(r.documentation_confirmed_at)}</TableCell>
                        <TableCell className="text-xs">
                          {r.section_completed_at ? (
                            <div className="flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3" /> {fmt(r.section_completed_at)}
                            </div>
                          ) : r.section_started_at ? (
                            <div className="flex items-center gap-1 text-amber-700">
                              <Clock className="w-3 h-3" /> in-progress · {fmt(r.section_started_at)}
                            </div>
                          ) : (
                            <span className="text-stone-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.account_received_at ? (
                            <div className="text-emerald-700">
                              <div>{fmt(r.account_received_at)}</div>
                              <div className="text-[10px] text-stone-500">by {r.account_received_by_name || 'system'}</div>
                            </div>
                          ) : (
                            <span className="text-stone-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>{statusBadge(r)}</TableCell>
                        <TableCell>
                          {r.is_completed ? (
                            <div className="flex items-center gap-1 text-xs text-emerald-700">
                              <CheckCircle2 className="w-4 h-4" /> Received
                            </div>
                          ) : !r.accounts_confirmable ? (
                            <div
                              className="flex items-center gap-2 opacity-70"
                              title={`${parcelLabel(r.parcel_type)} — no Accounts confirmation required`}
                              data-testid={`no-accounts-scan-${r.entry_id}`}
                            >
                              <div className="relative flex-1">
                                <ScanLine className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-300" />
                                <Input
                                  value=""
                                  disabled
                                  placeholder="Not applicable"
                                  className="pl-8 h-9 text-xs font-mono bg-stone-100 cursor-not-allowed"
                                  data-testid={`scan-input-${r.entry_id}`}
                                />
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                disabled
                                className="bg-stone-200 text-stone-400 cursor-not-allowed hover:bg-stone-200"
                                data-testid={`confirm-receipt-${r.entry_id}`}
                              >
                                Confirm
                              </Button>
                            </div>
                          ) : (
                            <form onSubmit={(e) => confirmReceipt(r, e)} className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <ScanLine className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                <Input
                                  value={scanInputs[r.entry_id] || ''}
                                  onChange={(e) => updateScan(r.entry_id, e.target.value)}
                                  placeholder="Scan document #"
                                  className="pl-8 h-9 text-xs font-mono"
                                  data-testid={`scan-input-${r.entry_id}`}
                                />
                              </div>
                              <Button
                                type="submit"
                                size="sm"
                                disabled={submittingId === r.entry_id}
                                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                                data-testid={`confirm-receipt-${r.entry_id}`}
                              >
                                {submittingId === r.entry_id ? '…' : 'Confirm'}
                              </Button>
                            </form>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination footer */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 text-sm">
              <div className="text-stone-600">
                {loading ? 'Loading…' : (
                  totalCount === 0 ? (
                    'No results'
                  ) : (
                    <>
                      Showing <span className="font-semibold">{(page - 1) * pageSize + 1}</span>
                      &nbsp;–&nbsp;
                      <span className="font-semibold">{Math.min(page * pageSize, totalCount)}</span>
                      &nbsp;of <span className="font-semibold">{totalCount}</span> entries
                      &nbsp;·&nbsp; page {page} / {totalPages || 1}
                    </>
                  )
                )}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-stone-500 m-0">Rows per page</Label>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => setPageSize(parseInt(v, 10) || 50)}
                >
                  <SelectTrigger className="h-9 w-[80px]" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[25, 50, 100, 200].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`page-size-${n}`}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline" size="icon" className="h-9 w-9"
                  onClick={() => setPage(1)}
                  disabled={loading || page <= 1}
                  data-testid="page-first"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-9 w-9"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={loading || page <= 1}
                  data-testid="page-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-9 w-9"
                  onClick={() => setPage((p) => Math.min(totalPages || 1, p + 1))}
                  disabled={loading || page >= (totalPages || 1)}
                  data-testid="page-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-9 w-9"
                  onClick={() => setPage(totalPages || 1)}
                  disabled={loading || page >= (totalPages || 1)}
                  data-testid="page-last"
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Image preview dialog */}
      <Dialog open={!!imgEntry} onOpenChange={(o) => !o && setImgEntry(null)}>
        <DialogContent className="max-w-3xl" data-testid="image-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="text-sm font-mono">{imgEntry}</span>
              <Button variant="ghost" size="icon" onClick={() => setImgEntry(null)}>
                <X className="w-4 h-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {imgLoading ? (
            <div className="py-12 text-center text-stone-500">
              <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Loading images…
            </div>
          ) : imgUrls.length === 0 ? (
            <div className="py-8 text-center text-stone-400">
              <AlertCircle className="w-5 h-5 inline mr-2" /> No images available.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
              {imgUrls.map((u, i) => (
                u ? (
                  <a
                    key={i}
                    href={u}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block border rounded overflow-hidden hover:shadow-md transition"
                    data-testid={`image-thumb-${i}`}
                  >
                    <img src={u} alt={`Doc image ${i + 1}`} className="w-full h-auto" />
                  </a>
                ) : (
                  <div key={i} className="p-6 border rounded text-center text-xs text-stone-400">
                    Failed to load image #{i + 1}
                  </div>
                )
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GateEntryDetails;
